// FilterBar.js
// This component renders a filter bar for vessel reporting, allowing users to filter data by trade direction,
// departure/destination countries and ports, date ranges, shipping companies, port operators, and markets.
// It integrates role-based access control to restrict filter options based on user roles.

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Paper,
  Chip,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getFilterOptions } from '../../../services/VoyageRepository';
import DateRangePickerWithShortcuts from '../../../../../shared/components/pickers/DateRangePickerWithShortcuts';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import useUserRoles from '../../../../../shared/hooks/useUserRoles';
import { useUserPreferences } from '../../../../../shared/contexts/UserPreferencesContext';
import { useScenario } from '../../../../../shared/contexts/ScenarioContext';

// ---------------------------------------------------------------------------
// Helper: sort an array of objects alphabetically by a given key
// ---------------------------------------------------------------------------
const sortByName = (arr, key = 'name') =>
  [...arr].sort((a, b) => (a[key] || '').localeCompare(b[key] || ''));

// ---------------------------------------------------------------------------
// Reusable component for selecting countries
// ---------------------------------------------------------------------------
const CountrySelect = ({
  label,
  value,
  onChange,
  countries,
  disabled = false,
  loading = false,
}) => (
  <Autocomplete
    fullWidth
    disabled={disabled || loading}
    loading={loading}
    options={sortByName(countries, 'name')}
    getOptionLabel={(option) => option.name || ''}
    value={countries.find((c) => c.countryCode === value) || null}
    onChange={(_, newValue) => onChange(newValue ? newValue.countryCode : '')}
    isOptionEqualToValue={(option, val) =>
      option.countryCode === val.countryCode
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant='outlined'
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading && <CircularProgress size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
    sx={{ marginBottom: '20px' }}
  />
);

// ---------------------------------------------------------------------------
// Reusable component for selecting ports
// ---------------------------------------------------------------------------
const PortSelect = ({
  label,
  value,
  onChange,
  ports,
  disabled = false,
  loading = false,
}) => (
  <Autocomplete
    fullWidth
    disabled={disabled || loading}
    loading={loading}
    options={sortByName(ports, 'name')}
    getOptionLabel={(option) => option.name || ''}
    value={ports.find((p) => p.unLocode === value) || null}
    onChange={(_, newValue) => onChange(newValue ? newValue.unLocode : '')}
    isOptionEqualToValue={(option, val) => option.unLocode === val.unLocode}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant='outlined'
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading && <CircularProgress size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  />
);

// ---------------------------------------------------------------------------
// Reusable component for selecting suppliers (shipping companies or port operators)
// ---------------------------------------------------------------------------
const SupplierSelect = ({
  label,
  value,
  onChange,
  suppliers,
  disabled = false,
  loading = false,
}) => (
  <Autocomplete
    fullWidth
    disabled={disabled || loading}
    loading={loading}
    options={sortByName(suppliers, 'supplierName')}
    getOptionLabel={(option) => option.supplierName || ''}
    value={
      suppliers.find((s) => s.supplierNo?.toString() === value?.toString()) ||
      null
    }
    onChange={(_, newValue) => onChange(newValue ? newValue.supplierNo : '')}
    isOptionEqualToValue={(option, val) =>
      option.supplierNo?.toString() === val.supplierNo?.toString()
    }
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant='outlined'
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading && <CircularProgress size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  />
);

// ---------------------------------------------------------------------------
// Reusable component for selecting ships
// ---------------------------------------------------------------------------
const ShipSelect = ({
  label,
  value,
  onChange,
  ships,
  disabled = false,
  loading = false,
}) => (
  <Autocomplete
    fullWidth
    disabled={disabled || loading}
    loading={loading}
    options={sortByName(ships, 'name')}
    getOptionLabel={(option) => `${option.imo} - ${option.name}`}
    value={ships.find((s) => s.imo === value) || null}
    onChange={(_, newValue) => onChange(newValue ? newValue.imo : '')}
    isOptionEqualToValue={(option, val) => option.imo === val.imo}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant='outlined'
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading && <CircularProgress size={20} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    )}
  />
);

// ---------------------------------------------------------------------------
// Main FilterBar component
// ---------------------------------------------------------------------------
const FilterBar = ({ onToggle, filters, setFilters }) => {
  const { preferences } = useUserPreferences();
  const dateFormat = preferences.dateFormat || 'DD.MM.YYYY';
  const { activeScenario } = useScenario();

  const [collapsed, setCollapsed] = useState(false);
  const [countries, setCountries] = useState({
    departure: [],
    destination: [],
  });
  const [ports, setPorts] = useState({ departure: [], destination: [] });
  const [shippingCompanies, setShippingCompanies] = useState([]);
  const [portOperators, setPortOperators] = useState([]);
  const [ships, setShips] = useState([]);
  const [marketCountries, setMarketCountries] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);

  const { supplier, country, hasUnrestrictedAccess, isMarketUser } =
    useUserRoles();

  // Debounce ref
  const debounceRef = useRef(null);

  // -----------------------------------------------------------------------
  // Fetch filter options whenever ANY filter changes (debounced 300ms)
  // -----------------------------------------------------------------------
  const filterOptionsParams = {};
  if (filters.departureCountry)
    filterOptionsParams.departureCountryCode = filters.departureCountry;
  if (filters.destinationCountry)
    filterOptionsParams.destinationCountryCode = filters.destinationCountry;
  if (filters.departurePort)
    filterOptionsParams.departurePort = filters.departurePort;
  if (filters.destinationPort)
    filterOptionsParams.destinationPort = filters.destinationPort;
  if (filters.shippingCompany)
    filterOptionsParams.shippingCompany = filters.shippingCompany;
  if (filters.portOperator)
    filterOptionsParams.portOperator = filters.portOperator;
  if (filters.imo) filterOptionsParams.imo = filters.imo;
  if (filters.market) filterOptionsParams.market = filters.market;
  if (filters.startDateETD)
    filterOptionsParams.startDateETD =
      filters.startDateETD.format('YYYY-MM-DD');
  if (filters.endDateETD)
    filterOptionsParams.endDateETD = filters.endDateETD.format('YYYY-MM-DD');
  if (filters.startDateETA)
    filterOptionsParams.startDateETA =
      filters.startDateETA.format('YYYY-MM-DD');
  if (filters.endDateETA)
    filterOptionsParams.endDateETA = filters.endDateETA.format('YYYY-MM-DD');
  if (activeScenario) filterOptionsParams.scenarioId = activeScenario.id;
  const filterOptionsKey = JSON.stringify(filterOptionsParams);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setIsOptionsLoading(true);
      const fetchFilterOptions = async () => {
        try {
          const params = JSON.parse(filterOptionsKey);

          const options = await getFilterOptions(
            Object.keys(params).length > 0 ? params : undefined
          );

          setCountries({
            departure: options.departureCountries || [],
            destination: options.destinationCountries || [],
          });

          setPorts({
            departure: params.departureCountryCode
              ? options.departurePorts || []
              : [],
            destination: params.destinationCountryCode
              ? options.destinationPorts || []
              : [],
          });

          setShippingCompanies(options.shippingCompanies || []);
          setPortOperators(options.portOperators || []);
          setShips(options.ships || []);
          setMarketCountries(options.markets || []);
        } catch (error) {
          console.error('Error fetching filter options:', error);
        } finally {
          setIsOptionsLoading(false);
        }
      };
      fetchFilterOptions();
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filterOptionsKey]);

  // -----------------------------------------------------------------------
  // Preselect filters based on user roles
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!country || !supplier) {
      console.warn('Missing user country or supplier in localStorage');
      return;
    }

    let needsUpdate = false;
    const updatedFilters = { ...filters };

    if (
      isMarketUser &&
      !hasUnrestrictedAccess &&
      updatedFilters.departureCountry !== country
    ) {
      updatedFilters.departureCountry = country;
      needsUpdate = true;
    }

    if (needsUpdate) setFilters(updatedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMarketUser,
    hasUnrestrictedAccess,
    country,
    supplier,
    setFilters,
    filters.departureCountry,
  ]);

  // -----------------------------------------------------------------------
  // Collapse / expand
  // -----------------------------------------------------------------------
  const toggleCollapse = () => setCollapsed((prev) => !prev);
  useEffect(() => onToggle(collapsed), [collapsed, onToggle]);

  // -----------------------------------------------------------------------
  // Filter change handlers
  // -----------------------------------------------------------------------
  const handleDeleteChip = useCallback(
    (chipType) => {
      setFilters((prev) => {
        const newFilters = {
          ...prev,
          [chipType]: chipType.includes('Date') ? null : '',
        };
        if (chipType === 'departureCountry') newFilters.departurePort = '';
        if (chipType === 'destinationCountry') newFilters.destinationPort = '';
        return newFilters;
      });
    },
    [setFilters]
  );

  const handleFilterChange = useCallback(
    (key, value) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        ...(key === 'departureCountry' && { departurePort: '' }),
        ...(key === 'destinationCountry' && { destinationPort: '' }),
      }));
    },
    [setFilters]
  );

  const handleResetAllFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      departureCountry: '',
      departurePort: '',
      destinationCountry: '',
      destinationPort: '',
      startDateETD: null,
      endDateETD: null,
      startDateETA: null,
      endDateETA: null,
      shippingCompany: '',
      portOperator: '',
      market: null,
      imo: null,
    }));
  }, [setFilters]);

  const handleDepartureCountryChange = (newValue) => {
    if (isMarketUser && !hasUnrestrictedAccess) {
      setFilters((prev) => ({
        ...prev,
        departureCountry: newValue,
        departurePort: '',
        ...(newValue &&
          newValue !== country && {
            destinationCountry: country,
            destinationPort: '',
          }),
      }));
    } else {
      handleFilterChange('departureCountry', newValue);
    }
  };

  const handleDestinationCountryChange = (newValue) => {
    if (isMarketUser && !hasUnrestrictedAccess) {
      setFilters((prev) => ({
        ...prev,
        destinationCountry: newValue,
        destinationPort: '',
        ...(newValue &&
          newValue !== country && {
            departureCountry: country,
            departurePort: '',
          }),
      }));
    } else {
      handleFilterChange('destinationCountry', newValue);
    }
  };

  // -----------------------------------------------------------------------
  // Active filter chips
  // -----------------------------------------------------------------------
  const chips = useMemo(
    () =>
      [
        filters.departureCountry && {
          label: `Dep. Country: ${countries.departure.find((c) => c.countryCode === filters.departureCountry)?.name || 'Unknown'}`,
          type: 'departureCountry',
        },
        filters.departurePort && {
          label: `Dep. Port: ${ports.departure.find((p) => p.unLocode === filters.departurePort)?.name || 'Unknown'}`,
          type: 'departurePort',
        },
        filters.destinationCountry && {
          label: `Dest. Country: ${countries.destination.find((c) => c.countryCode === filters.destinationCountry)?.name || 'Unknown'}`,
          type: 'destinationCountry',
        },
        filters.destinationPort && {
          label: `Dest. Port: ${ports.destination.find((p) => p.unLocode === filters.destinationPort)?.name || 'Unknown'}`,
          type: 'destinationPort',
        },
        filters.startDateETD && {
          label: `Start ETD: ${filters.startDateETD.format(dateFormat)}`,
          type: 'startDateETD',
        },
        filters.endDateETD && {
          label: `End ETD: ${filters.endDateETD.format(dateFormat)}`,
          type: 'endDateETD',
        },
        filters.startDateETA && {
          label: `Start ETA: ${filters.startDateETA.format(dateFormat)}`,
          type: 'startDateETA',
        },
        filters.endDateETA && {
          label: `End ETA: ${filters.endDateETA.format(dateFormat)}`,
          type: 'endDateETA',
        },
        filters.shippingCompany && {
          label: `Ship Co.: ${shippingCompanies.find((sc) => sc.supplierNo.toString() === filters.shippingCompany.toString())?.supplierName || 'Unknown'}`,
          type: 'shippingCompany',
        },
        filters.portOperator && {
          label: `Port Op.: ${portOperators.find((po) => po.supplierNo.toString() === filters.portOperator.toString())?.supplierName || 'Unknown'}`,
          type: 'portOperator',
        },
        filters.market && {
          label: `Market: ${marketCountries.find((c) => c.countryCode === filters.market)?.name || 'Unknown'}`,
          type: 'market',
        },
        filters.imo && {
          label: `Ship: ${ships.find((s) => s.imo === filters.imo)?.name || 'Unknown'} (${filters.imo})`,
          type: 'imo',
        },
      ].filter(Boolean),
    [
      filters,
      countries.departure,
      countries.destination,
      ports.departure,
      ports.destination,
      shippingCompanies,
      portOperators,
      ships,
      marketCountries,
      dateFormat,
    ]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Paper elevation={2} sx={{ borderRadius: '6px', height: '100%' }}>
      <Box
        sx={{
          width: collapsed ? '50px' : '220px',
          transition: 'width 0.3s',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
          height: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: collapsed ? 'center' : 'space-between',
            padding: '0 10px',
          }}
        >
          {!collapsed && <Typography variant='h3'>Filters</Typography>}
          <IconButton onClick={toggleCollapse}>
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {collapsed && (
          <Typography
            variant='h3'
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              marginTop: '20px',
            }}
          >
            Filters
          </Typography>
        )}

        {/* Expanded filter controls */}
        {!collapsed && (
          <Box sx={{ width: '100%', padding: '10px' }}>
            {/* Trade direction toggle */}
            <Typography marginBottom={1} variant='h6'>
              Trade Direction Filter
            </Typography>
            <ToggleButtonGroup
              color='primary'
              value={filters.groupBy}
              exclusive
              onChange={(event, newStatus) =>
                handleFilterChange('groupBy', newStatus)
              }
              aria-label='Shipment Status'
              sx={{ marginBottom: '20px' }}
            >
              <ToggleButton value='departure'>Export</ToggleButton>
              <ToggleButton value='destination'>Import</ToggleButton>
            </ToggleButtonGroup>

            {/* Departure */}
            <Typography marginBottom={1} variant='h6'>
              Departure
            </Typography>
            <CountrySelect
              label='Departure Country'
              value={filters.departureCountry}
              onChange={handleDepartureCountryChange}
              countries={countries.departure}
              loading={isOptionsLoading}
            />
            <PortSelect
              label='Departure Port'
              value={filters.departurePort}
              onChange={(value) => handleFilterChange('departurePort', value)}
              ports={ports.departure}
              disabled={!filters.departureCountry || isOptionsLoading}
              loading={isOptionsLoading}
            />

            {/* Destination */}
            <Typography variant='h6' marginTop={2} marginBottom={1}>
              Destination
            </Typography>
            <CountrySelect
              label='Destination Country'
              value={filters.destinationCountry}
              onChange={handleDestinationCountryChange}
              countries={countries.destination}
              loading={isOptionsLoading}
            />
            <PortSelect
              label='Destination Port'
              value={filters.destinationPort}
              onChange={(value) => handleFilterChange('destinationPort', value)}
              ports={ports.destination}
              disabled={!filters.destinationCountry || isOptionsLoading}
              loading={isOptionsLoading}
            />

            {/* ETD date range */}
            <Typography variant='body1' marginTop={2}>
              ETD Date Range
            </Typography>
            <DateRangePickerWithShortcuts
              startDate={filters.startDateETD}
              endDate={filters.endDateETD}
              onChange={([start, end]) => {
                handleFilterChange('startDateETD', start);
                handleFilterChange('endDateETD', end);
              }}
            />

            {/* ETA date range */}
            <Typography variant='body1' marginTop={2}>
              ETA Date Range
            </Typography>
            <DateRangePickerWithShortcuts
              startDate={filters.startDateETA}
              endDate={filters.endDateETA}
              onChange={([start, end]) => {
                handleFilterChange('startDateETA', start);
                handleFilterChange('endDateETA', end);
              }}
            />

            {/* Shipping company */}
            <Typography marginBottom={1} marginTop={2} variant='h6'>
              Shipping Company
            </Typography>
            <SupplierSelect
              label='Shipping Company'
              value={filters.shippingCompany}
              onChange={(value) => handleFilterChange('shippingCompany', value)}
              suppliers={shippingCompanies}
              loading={isOptionsLoading}
            />

            {/* Port operator */}
            <Typography marginBottom={1} marginTop={2} variant='h6'>
              Port Operator
            </Typography>
            <SupplierSelect
              label='Port Operator'
              value={filters.portOperator}
              onChange={(value) => handleFilterChange('portOperator', value)}
              suppliers={portOperators}
              loading={isOptionsLoading}
            />

            {/* Ship */}
            <Typography marginBottom={1} marginTop={2} variant='h6'>
              Ship
            </Typography>
            <ShipSelect
              label='Ship'
              value={filters.imo}
              onChange={(value) => handleFilterChange('imo', value)}
              ships={ships}
              loading={isOptionsLoading}
            />

            {/* Market */}
            <Typography marginBottom={1} marginTop={2} variant='h6'>
              Market
            </Typography>
            <CountrySelect
              label='Market'
              value={filters.market || ''}
              onChange={(value) => handleFilterChange('market', value)}
              countries={marketCountries}
              loading={isOptionsLoading}
            />

            {/* Reset All Filters */}
            {chips.length > 0 && (
              <Button
                variant='outlined'
                size='small'
                fullWidth
                onClick={handleResetAllFilters}
                sx={{ mt: 1 }}
              >
                Reset All Filters
              </Button>
            )}
          </Box>
        )}

        {/* Active filter chips */}
        {!collapsed && (
          <Box sx={{ width: '100%', padding: '10px' }}>
            <Stack direction='column' spacing={1}>
              {chips.map((chip) => (
                <Chip
                  key={chip.type}
                  label={chip.label}
                  variant='outlined'
                  onDelete={() => handleDeleteChip(chip.type)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default FilterBar;
